/**
 * saasService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Intelligent Template Marketplace & Subscription Service.
 * Implements Tiered Subscription (with upfront Microsoft SaaS trial), Metered Billing
 * (single templates & lifetime category packs), custom XML-based ownership metadata, 
 * access gating for collaboration, and database storage syncing.
 */

import { db } from '../config';
import { getUserPlan } from './subscription';
import {
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';

// ─── SaaS Database Schemas & Types ───────────────────────────────────────────

export interface SubscriptionRecord {
  uid: string;
  plan: 'Free' | 'Personal' | 'Professional' | 'enterprise';
  status: 'active' | 'trial' | 'cancelled' | 'suspended';
  trialStartDate?: number;
  trialEndDate?: number;
  credits: number; // monthly template allowance (reset on 1st of every month)
  creditsLastReset: number; // timestamp
  msSaasSubscriptionId?: string; // Microsoft Transactable SaaS ID
}

export interface MeteredUsageRecord {
  id: string;
  uid: string;
  templateId: string;
  purchasedAt: number;
  price: number; // $4.99 dimension in cents = 499
  dimension: 'single_template_unlock';
}

export interface LifetimeUnlockRecord {
  id: string;
  uid: string;
  categoryId: string; // e.g. "Strategic Planning", "Project Management"
  unlockedAt: number;
  price: number; // $24.99 dimension in cents = 2499
  accessType: 'Lifetime';
}

// ─── 1. Subscription & Trial Logic ───────────────────────────────────────────

/**
 * Starts a 7-Day Free Trial for either Personal ($5.99/mo) or Professional ($9.99/mo) plan.
 * Requires upfront Microsoft saas payment billing configuration.
 */
export async function startPro7DayTrial(uid: string, targetPlan: 'Personal' | 'Professional' = 'Professional'): Promise<SubscriptionRecord> {
  const now = Date.now();
  const trialDays = 7;
  const trialEnd = now + trialDays * 24 * 60 * 60 * 1000;

  const newSub: SubscriptionRecord = {
    uid,
    plan: targetPlan,
    status: 'trial',
    trialStartDate: now,
    trialEndDate: trialEnd,
    credits: targetPlan === 'Professional' ? 999 : 0, // unlimited templates for Professional trial, 0 for Personal
    creditsLastReset: now,
    msSaasSubscriptionId: 'sub_ms_saas_' + Math.random().toString(36).substring(2, 11)
  };

  await setDoc(doc(db, 'subscriptions', uid), newSub);
  
  // Also sync in core users table
  await setDoc(doc(db, 'users', uid), {
    plan: targetPlan,
    subscriptionStatus: 'active',
  }, { merge: true });

  return newSub;
}

/**
 * Gets or initializes a user's subscription record.
 * Handles the monthly template credits reset if a new month has started.
 */
export async function getSubscriptionRecord(uid: string): Promise<SubscriptionRecord> {
  const docRef = doc(db, 'subscriptions', uid);
  const snap = await getDoc(docRef);

  // Read primary user plan from 'users' collection
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  let userPlan: 'Free' | 'Personal' | 'Professional' | 'enterprise' = 'Free';
  let subscriptionStatus: 'active' | 'trial' | 'cancelled' | 'suspended' = 'active';

  if (userSnap.exists()) {
    const userData = userSnap.data();
    const p = userData?.plan as string | undefined;
    const s = userData?.subscriptionStatus as string | undefined;

    if (p === 'Personal' || p === 'personal-monthly' || p === 'personal-annual') {
      userPlan = 'Personal';
    } else if (p === 'Professional' || p === 'professional-monthly' || p === 'professional-annual' || p === 'Personal Pro') {
      userPlan = 'Professional';
    } else if (p === 'enterprise' || p === 'Enterprise') {
      userPlan = 'enterprise';
    }

    if (s === 'active' || s === 'trial' || s === 'cancelled' || s === 'suspended') {
      subscriptionStatus = s;
    }
  }

  if (!snap.exists()) {
    // New aligned subscription
    const alignedSub: SubscriptionRecord = {
      uid,
      plan: userPlan,
      status: subscriptionStatus,
      credits: userPlan === 'Professional' || userPlan === 'enterprise' ? 999 : 0,
      creditsLastReset: Date.now()
    };
    await setDoc(docRef, alignedSub);
    return alignedSub;
  }

  let sub = snap.data() as SubscriptionRecord;

  // Sync if different
  if (sub.plan !== userPlan || sub.status !== subscriptionStatus) {
    sub.plan = userPlan;
    sub.status = subscriptionStatus;
    sub.credits = userPlan === 'Professional' || userPlan === 'enterprise' ? 999 : sub.credits;
    await updateDoc(docRef, {
      plan: userPlan,
      status: subscriptionStatus,
      credits: sub.credits
    });
  }

  // Monthly credits reset logic (triggers on 1st of every month)
  if (sub.plan === 'Personal Pro' || sub.status === 'trial') {
    const lastResetDate = new Date(sub.creditsLastReset || Date.now());
    const currentDate = new Date();
    
    // Check if we are in a different month/year from last reset
    const isNewMonth = 
      currentDate.getMonth() !== lastResetDate.getMonth() || 
      currentDate.getFullYear() !== lastResetDate.getFullYear();

    if (isNewMonth) {
      // It is the 1st of a new month! Reset template allowance to 5
      sub.credits = 5;
      sub.creditsLastReset = Date.now();
      await updateDoc(docRef, {
        credits: 5,
        creditsLastReset: sub.creditsLastReset
      });
    }
  }

  return sub;
}

// ─── 2. Template Marketplace (Metered & Lifetime Purchases) ──────────────────

/**
 * Checks if a user has unlocked a single template (using active Professional/Enterprise
 * subscription, trial, single template buy, or category lifetime pack).
 */
export async function checkTemplateAccess(
  uid: string, 
  templateId: string, 
  categoryName: string
): Promise<{ unlocked: boolean; source: 'Free' | 'ProSubscription' | 'SubscriberCredit' | 'SinglePurchase' | 'CategoryLifetime' }> {
  // 1. Staging / Beta override (always fully unlocked)
  if (process.env.VITE_ENVIRONMENT === 'staging') {
    return { unlocked: true, source: 'ProSubscription' };
  }

  // 2. Core Blank note is always unlocked for any active subscriber
  if (templateId === 'tpl-general-blank') {
    return { unlocked: true, source: 'Free' };
  }

  // 3. Professional / Enterprise get ALL templates free. We use getUserPlan (the same
  // resolver the app paywall uses) rather than the raw Firestore plan, so it also
  // covers the universal 30-day trial (resolves to Professional via createdAt) and
  // OWNER_EMAILS — keeping templates consistent with the full-Professional trial
  // experience. Personal & post-trial Free users fall through and must pay.
  const plan = await getUserPlan(uid);
  if (plan === 'Professional' || plan === 'enterprise') {
    return { unlocked: true, source: 'ProSubscription' };
  }

  // 5. Category Lifetime Check (Category Packs lifetime buy)
  const isCategoryUnlocked = await checkCategoryLifetimeUnlock(uid, categoryName);
  if (isCategoryUnlocked) {
    return { unlocked: true, source: 'CategoryLifetime' };
  }

  // 6. Individual Single Template Purchase check ($4.99 buy)
  const singleQuery = query(
    collection(db, 'metered_usage'),
    where('uid', '==', uid),
    where('templateId', '==', templateId)
  );
  const singleSnap = await getDocs(singleQuery);
  if (!singleSnap.empty) {
    return { unlocked: true, source: 'SinglePurchase' };
  }

  return { unlocked: false, source: 'Free' };
}

/**
 * Purchases a single template for $4.99 via Metered billing.
 */
export async function purchaseSingleTemplate(uid: string, templateId: string): Promise<MeteredUsageRecord> {
  const recordId = `metered_${uid}_${templateId}`;
  const record: MeteredUsageRecord = {
    id: recordId,
    uid,
    templateId,
    purchasedAt: Date.now(),
    price: 499, // $4.99 in cents
    dimension: 'single_template_unlock'
  };

  await setDoc(doc(db, 'metered_usage', recordId), record);
  return record;
}

/**
 * Purchases a Category Lifetime Pack for $24.99 (one-time Lifetime fee).
 * Sets Access_Type to "Lifetime" in Azure SQL / database rules.
 */
export async function purchaseCategoryLifetimePack(uid: string, categoryName: string): Promise<LifetimeUnlockRecord> {
  const id = `lifetime_${uid}_${categoryName.replace(/\s+/g, '_').toLowerCase()}`;
  const record: LifetimeUnlockRecord = {
    id,
    uid,
    categoryId: categoryName,
    unlockedAt: Date.now(),
    price: 2499, // $24.99 in cents
    accessType: 'Lifetime'
  };

  await setDoc(doc(db, 'lifetime_unlocks', id), record);
  return record;
}

/**
 * Checks if the user owns the Category Lifetime Pack.
 */
export async function checkCategoryLifetimeUnlock(uid: string, categoryName: string): Promise<boolean> {
  const id = `lifetime_${uid}_${categoryName.replace(/\s+/g, '_').toLowerCase()}`;
  const snap = await getDoc(doc(db, 'lifetime_unlocks', id));
  if (snap.exists()) {
    const data = snap.data() as LifetimeUnlockRecord;
    return data.accessType === 'Lifetime';
  }
  return false;
}

// ─── 3. Custom XML Part Ownership Metadata & Collaboration Gate ──────────────

const XML_NAMESPACE = "http://schemas.marginpro.com/metadata";

/**
 * Tags the active Word Document with the Creator_ID using Custom XML.
 */
export async function tagDocumentWithCreator(creatorId: string): Promise<void> {
  if (typeof Office === "undefined" || !Office?.context?.document) return;

  await Word.run(async (context) => {
    const customXmlParts = context.document.customXmlParts;
    const parts = customXmlParts.getByNamespace(XML_NAMESPACE);
    context.load(parts, "items");
    await context.sync();

    // XML schema representation of document meta
    const xmlContent = `<Metadata xmlns="${XML_NAMESPACE}"><CreatorID>${creatorId}</CreatorID><CreatedTimestamp>${Date.now()}</CreatedTimestamp></Metadata>`;

    if (parts.items.length === 0) {
      customXmlParts.add(xmlContent);
    } else {
      // Update existing if present
      parts.items[0].delete();
      customXmlParts.add(xmlContent);
    }
    await context.sync();
  }).catch(err => {
    console.error('[CustomXML] Failed to write creator metadata:', err);
  });
}

/**
 * Reads the Creator_ID metadata from the document's Custom XML.
 */
export async function getDocumentCreatorId(): Promise<string | null> {
  if (typeof Office === "undefined" || !Office?.context?.document) return null;

  return await Word.run(async (context) => {
    const customXmlParts = context.document.customXmlParts;
    const parts = customXmlParts.getByNamespace(XML_NAMESPACE);
    context.load(parts, "items");
    await context.sync();

    if (parts.items.length > 0) {
      const xmlPart = parts.items[0];
      const xmlGet = xmlPart.getXml();
      await context.sync();
      
      const xmlText = xmlGet.value;
      const match = xmlText.match(/<CreatorID>([^<]+)<\/CreatorID>/);
      return match ? match[1] : null;
    }
    return null;
  }).catch(err => {
    console.warn('[CustomXML] Could not fetch creator ID:', err);
    return null;
  });
}
